// Format fingerprint — a stable SHA-256 over a file's structural shape,
// independent of (and computed regardless of) the AI audit. The hash inputs are:
//   normalized header row  ('::')  ordered column-kind sequence
// where each header cell is lowercased + trimmed and joined with '|', and the
// column kinds come from the profiler in column order. Populated on every
// telemetry insert where profiling ran, so Parser Health can group parses by
// format and (Batch C+) cache audit recipes per fingerprint.

/**
 * Compute the hex-encoded SHA-256 format fingerprint. Returns null when
 * profiling did not run (no column kinds) or Web Crypto is unavailable — the
 * fingerprint is observational and must never block telemetry.
 */
export async function computeFormatFingerprint(
  headerRow: (string | null)[] | null,
  columnKinds: string[] | null,
): Promise<string | null> {
  if (!columnKinds || columnKinds.length === 0) return null;

  const normalizedHeader = (headerRow ?? [])
    .map(h => (h ?? '').toLowerCase().trim())
    .join('|');
  const input = `${normalizedHeader}::${columnKinds.join('|')}`;

  try {
    const bytes = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Non-secure context / crypto.subtle missing — fingerprint is optional.
    return null;
  }
}
